<template>
  <ADrawer
    :width="drawerWidth"
    :open="open"
    class="residency-residents-filter-drawer"
    :body-style="{ paddingBottom: '24px' }"
    :footer-style="{ textAlign: 'center', padding: '16px 48px 32px 48px' }"
    @close="emit('update:open', false)"
  >
    <template #title>
      <div class="residency-residents-filter-drawer__header-shell">
        <div class="residency-residents-filter-drawer__header-overline">Фильтр</div>
      </div>
    </template>

    <template #closeIcon>
      <span class="residency-residents-filter-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <AForm layout="vertical" class="residency-residents-filter-drawer__form">
      <div class="residency-residents-filter-drawer__fields-section">
        <AFormItem label="Кластер" class="stc-ant-field-skin">
          <ASelect
            v-model:value="draft.cluster"
            :options="getSelectOptions('residencyResidentsCluster')"
            placeholder="Все кластеры"
          />
        </AFormItem>
      </div>

      <div class="residency-residents-filter-drawer__fields-section">
        <AFormItem label="Направление" class="stc-ant-field-skin">
          <ASelect
            v-model:value="draft.direction"
            :options="getSelectOptions('residencyResidentsDirection')"
            placeholder="Все направления"
          />
        </AFormItem>
      </div>
    </AForm>

    <template #footer>
      <div class="residency-residents-filter-drawer__footer-container">
        <button
          type="button"
          class="btn btn-outline btn-medium"
          :disabled="isResetDisabled"
          @click="handleReset"
        >
          Сбросить
        </button>
        <button
          type="button"
          class="btn btn-primary btn-medium"
          :disabled="isApplyDisabled"
          @click="handleApply"
        >
          Применить
        </button>
      </div>
    </template>
  </ADrawer>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import {
  Drawer as ADrawer,
  Form as AForm,
  FormItem as AFormItem,
  Select as ASelect,
} from 'ant-design-vue';

import {
  applyResidencyResidentsFilterDraft,
  draftsEqual,
  getResidencyResidentsScope,
  readResidencyResidentsFilterDraft,
  getSelectOptions,
  type ResidencyResidentsFilterDraft,
} from './sync-filters';

const props = defineProps<{
  open: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'applied'): void;
}>();

const applied = ref<ResidencyResidentsFilterDraft>({
  cluster: '',
  direction: '',
});

const draft = reactive<{
  cluster: string;
  direction: string;
}>({
  cluster: '',
  direction: '',
});

const viewportWidth = ref(window.innerWidth);

const drawerWidth = computed(() => (viewportWidth.value <= 576 ? '100%' : 800));

const hasDraftValues = computed(() =>
  Boolean(draft.cluster || draft.direction),
);

const hasChanges = computed(
  () =>
    draftsEqual(
      {
        cluster: draft.cluster,
        direction: draft.direction,
      },
      applied.value,
    ) === false,
);

const isResetDisabled = computed(() => !hasDraftValues.value);
const isApplyDisabled = computed(() => !hasChanges.value);

function syncDraftFromDom(): void {
  const current = readResidencyResidentsFilterDraft(getResidencyResidentsScope());

  draft.cluster = current.cluster;
  draft.direction = current.direction;
  applied.value = {
    cluster: current.cluster,
    direction: current.direction,
  };
}

function handleResize(): void {
  viewportWidth.value = window.innerWidth;
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    syncDraftFromDom();
  },
);

function handleReset(): void {
  draft.cluster = '';
  draft.direction = '';
}

async function handleApply(): Promise<void> {
  const scope = getResidencyResidentsScope();
  const nextDraft: ResidencyResidentsFilterDraft = {
    cluster: draft.cluster,
    direction: draft.direction,
  };

  applyResidencyResidentsFilterDraft(scope, nextDraft);

  applied.value = nextDraft;
  emit('applied');
  emit('update:open', false);
}

onMounted(() => {
  syncDraftFromDom();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>
